import { AxiosRequestConfig } from "axios";
import { axiosInstance } from "../../utils/axios";
import { Buffer } from "buffer";
import { DocumentPart } from "../models";
import { DocumentPartMetadata } from "../models/document-part-metadata";
import { StructureDocumentPart } from "../models/structure-document-part.ts";
import { TgkElaborationResponse } from "../models/tgk-elaboration-response";
import { TgkChangeDocStructureElaborationRequest } from "../models/tgk-change-doc-structure-elaboration-request";
import { EmbeddedRoots } from "../models/embedded-roots";
import { DocumentRoot } from "../models/document-root";
import { PageDocumentRoots } from "../models/page-document-roots";
import { DocumentPermissionTO } from "../models/document-permission-to";
import { DocumentPartIdNamePair } from "../models/document-part-id-name-pair";
import { DocumentPosition } from "../models/document-position";
import { DocumentPartWithStatus } from "../models/document-part-with-status";
import { DocumentPartsStatus } from "../models/document-parts-status";
import { UserApprover } from "../models/user-approver";
import { DimensionParameter } from "../models/dimension-parameter";
import { XmlDocumentMetadata } from "../models/xml-document-metadata";
import { TgkDocumentElaborationRequest } from "../models/tgk-document-elaboration-request";
import { DocumentPartSettings } from "../models/document-part-settings";
import { DocumentPartWithSuperUser } from "../models/document-part-with-super-user";
import { UserDocumentCheckout } from "../models/user-document-checkout";
import { OutlineFilterStatus } from "../../components/cpm/workflowStatusCategory";
import { DocumentPartVersion } from "../models/document-part-version";
import { ComponentInstancesResult } from "../models/component-instances-result";
import { ComponentSourceMappingPair } from "../models/component-source-mapping-pair";
import { InstanceHint } from "../models/instance-hint";
import { ElaborationItem, ElaborationRequest, ElaborationResponse } from "../models";
import * as uuid from "uuid";
import { store } from "../../redux/store/store";
import { selectClientId, selectCpmUsername } from "../../redux/feature/settings/configurations-slice";
import {
  createElaborationRequestAsync,
  selectActiveEventChannelId,
} from "../../redux/feature/object/remote-elaborations-slice";
import { EVENT_CHANNEL_DEFAULT_TOPIC } from "../../utils/constants/eventChannel";

// For testing purpose
//export const ITEMS_PER_PAGE = 2;
export const ITEMS_PER_PAGE = 20;

export interface CallbackMappingMessage {
  status: string;
  details: string[];
}

const DOCUMENT_PART_BASE_PATH: string = "/documentpart";
const DOCUMENT_PART_METADATA_BASE_PATH: string = "/documentpartmetadata";
const COMPONENTS_BASE_PATH: string = "/components";

// Number of component instances requested from /components/instances: this story only needs to know
// whether at least one instance exists (IDS-7053 decision U2), never the instances themselves (IDS-7054).
const COMPONENT_INSTANCES_EXISTENCE_LIMIT = 1;

// Per-documentPartId session cache for fetchDocumentPartVersion, keyed by the same id the endpoint takes.
// ChangeComponentSource fetches this before the expensive instance lookup when the page mounts, then invalidates
// the entry when the page unmounts so returning after a document migration performs a fresh check. The promise
// itself (not just the resolved value) is cached so overlapping calls for the same id collapse into one request;
// a rejected request is evicted so Retry can issue a new request after a transient failure.
const documentPartVersionCache = new Map<string, Promise<DocumentPartVersion>>();

export async function downloadDocumentPart(documentId: string) {
  const response = await axiosInstance.get(`${DOCUMENT_PART_BASE_PATH}/${documentId}/download`, {
    responseType: "arraybuffer",
  });

  if (response.status !== 200) {
    throw new Error("Error getting content: " + response.statusText + " (" + response.status + ")");
  }
  return Buffer.from(response.data, "binary").toString("base64");
}

export async function fetchDocumentPartNode(Id: string): Promise<DocumentPart[]> {
  try {
    const resp = await axiosInstance.get(`${DOCUMENT_PART_BASE_PATH}/${Id}/children`);
    return resp.data;
  } catch (reason) {
    console.log(`Error on fetching remote data: ${reason}`);
  }
}

export async function fetchDocumentPartNodeWithPermissions(
  Id: string,
  page: number,
  config?: AxiosRequestConfig
): Promise<EmbeddedRoots> {
  try {
    const resp = await axiosInstance.get(
      `${DOCUMENT_PART_BASE_PATH}/${Id}/childrenwithpermissions?page=${page}&size=${ITEMS_PER_PAGE}`,
      config
    );
    return resp.data;
  } catch (reason) {
    console.log(`Error on fetching remote data: ${reason}`);
  }
}

export async function fetchDocumentPartChildrenWithPermissions(
  items: { parentId: string; anchorChildId?: string }[]
): Promise<Record<string, EmbeddedRoots>> {
  const resp = await axiosInstance.post(`${DOCUMENT_PART_BASE_PATH}/childrenwithpermissions`, items);
  const data: Record<string, { content?: DocumentRoot[]; page: PageDocumentRoots }> = resp.data ?? {};

  const result: Record<string, EmbeddedRoots> = {};
  for (const [parentId, entry] of Object.entries(data)) {
    result[parentId] = {
      _embedded: { content: entry.content ?? [] },
      page: entry.page,
    };
  }
  return result;
}

export async function searchDocumentParts(
  rootId: string,
  query: string,
  page: number,
  size: number = ITEMS_PER_PAGE,
  signal?: AbortSignal,
  filter?: { assignedToMe?: boolean; status?: OutlineFilterStatus | null }
): Promise<EmbeddedRoots<DocumentPermissionTO>> {
  const encodedRootId = encodeURIComponent(rootId);
  const encodedQuery = encodeURIComponent(query);
  let url = `${DOCUMENT_PART_BASE_PATH}/${encodedRootId}/search?query=${encodedQuery}&page=${page}&size=${size}`;
  if (filter?.assignedToMe === true) {
    url += `&assignedTo=CURRENT_USER`;
  }
  if (filter?.status !== undefined && filter?.status !== null) {
    url += `&status=${filter.status}`;
  }
  const resp = await axiosInstance.get(url, { signal, params: { notShowLoading: true } });
  return resp.data;
}

export async function fetchDocumentPartStatus(
  rootId: string,
  documentIds: string[]
): Promise<DocumentPartWithStatus[]> {
  try {
    const resp = await axiosInstance.post(
      `${DOCUMENT_PART_BASE_PATH}/rootId/${rootId}/workflowActionStatus`,
      documentIds,
      {
        params: { notShowLoading: true },
      }
    );
    return resp.data;
  } catch (reason) {
    console.log(`Error on fetching remote data: ${reason}`);
  }
}

export async function fetchWorkflowStatusForRoots(documentIds: string[]): Promise<DocumentPartWithStatus[]> {
  if (documentIds.length === 0) {
    return [];
  }
  try {
    const resp = await axiosInstance.post(`${DOCUMENT_PART_BASE_PATH}/workflowActionStatus`, documentIds, {
      params: { notShowLoading: true, notShowError: true },
    });
    return resp.data;
  } catch (reason) {
    console.log(`Error on fetching remote data: ${reason}`);
    return [] as DocumentPartWithStatus[];
  }
}

export async function fetchActionWorkflow(
  rootId: string,
  Id: string,
  op: string,
  comment: string,
  userName?: string
): Promise<DocumentPartWithStatus[]> {
  const url = `${DOCUMENT_PART_BASE_PATH}/${Id}/root/${rootId}/workflowaction/${op}`;
  const call = (notShowError: boolean) =>
    axiosInstance.post(
      url,
      {},
      {
        params: {
          comment,
          ...(userName ? { userName } : {}),
          notShowLoading: true,
          notShowError,
          invalidateCache: ["alldocumentpartstatus"],
        },
      }
    );
  try {
    const resp = await call(false);
    return resp.data;
  } catch (err: any) {
    const status = err?.response?.status;

    if (status === 503) {
      const retry = await call(true);
      return retry.data;
    }
    throw err;
  }
}

async function fetchDirectChildrenWorkflowAction(
  rootId: string,
  Id: string,
  op: "submit" | "approve",
  comment: string,
  userName?: string
): Promise<DocumentPartWithStatus[]> {
  const url = `${DOCUMENT_PART_BASE_PATH}/${Id}/root/${rootId}/workflowactions/directchildren/${op}`;
  const call = (notShowError: boolean) =>
    axiosInstance.post(
      url,
      {},
      {
        params: {
          comment,
          ...(userName ? { userName } : {}),
          notShowLoading: true,
          notShowError,
        },
      }
    );
  try {
    const resp = await call(false);
    return resp.data;
  } catch (err: any) {
    const status = err?.response?.status;

    if (status === 503) {
      const retry = await call(true);
      return retry.data;
    }
    throw err;
  }
}

export function fetchSubmitWorkflowForDirectChildren(
  rootId: string,
  Id: string,
  comment: string,
  userName?: string
): Promise<DocumentPartWithStatus[]> {
  return fetchDirectChildrenWorkflowAction(rootId, Id, "submit", comment, userName);
}

export function fetchApproveWorkflowForDirectChildren(
  rootId: string,
  Id: string,
  comment: string,
  userName?: string
): Promise<DocumentPartWithStatus[]> {
  return fetchDirectChildrenWorkflowAction(rootId, Id, "approve", comment, userName);
}

export async function fetchDocumentPartDetails(Id: string, config?: AxiosRequestConfig): Promise<DocumentPart> {
  try {
    const resp = await axiosInstance.get(`${DOCUMENT_PART_BASE_PATH}/${Id}`, {
      ...config,
      params: { generatesElaboration: true, notShowLoading: true, ...config?.params },
    });
    return resp.data;
  } catch (reason) {
    console.log(`Error on fetching remote data: ${reason}`);
  }
}

export async function fetchActiveDimensionParameters(Id: string): Promise<DimensionParameter[]> {
  try {
    const resp = await axiosInstance.get(`${DOCUMENT_PART_BASE_PATH}/${Id}/parameters`);
    return resp.data;
  } catch (reason) {
    console.log(`Error on fetching remote data: ${reason}`);
  }
}

export async function fetchManageDocumentPartOutline(id: string): Promise<StructureDocumentPart> {
  try {
    const resp = await axiosInstance.get(`structuredocumentpart/${id}`);
    return resp.data;
  } catch (reason) {
    console.log(`Error on fetching remote data: ${reason}`);
  }
}

export async function fetchChangeDocumentPartStructure(
  structureDocumentPart: TgkChangeDocStructureElaborationRequest
): Promise<TgkElaborationResponse> {
  const resp = await axiosInstance.post(`structuredocumentpart/changedocumentstructure`, structureDocumentPart, {
    params: { generatesElaboration: true },
  });
  return resp.data;
}

export function fetchExpandedDocumentPartOutline(id: string): Promise<DocumentPart> {
  return axiosInstance
    .get(`${DOCUMENT_PART_BASE_PATH}/expandedoutline/${id}`)
    .then((resp) => resp.data)
    .catch((reason) => console.log(`Error on fetching remote data: ${reason}`));
}

export function fetchRootAndMultilinkedDescendants(documentId: string): Promise<DocumentPart[]> {
  return axiosInstance
    .get(`${DOCUMENT_PART_BASE_PATH}/flatmultilinked/${documentId}`)
    .then((resp) => resp.data)

    .catch((reason) => console.log(`Error on fetching remote data: ${reason}`));
}

export async function fetchDriveItemName(driveItemId: string): Promise<string> {
  try {
    const resp = await axiosInstance.get(`${DOCUMENT_PART_BASE_PATH}/${driveItemId}/sharepointname`, {
      params: { notShowLoading: true },
    });
    return resp.data;
  } catch (reason) {
    console.log(`Error on fetching remote data: ${reason}`);
  }
}

export async function editDocumentPart(
  tgkDocumentElaborationRequest: TgkDocumentElaborationRequest
): Promise<XmlDocumentMetadata> {
  const resp = await axiosInstance.put(`${DOCUMENT_PART_BASE_PATH}/edit`, tgkDocumentElaborationRequest, {
    params: { generatesElaboration: true },
  });
  return resp.data;
}

export async function alignDocumentPart(tgkDocumentElaborationRequest: TgkDocumentElaborationRequest): Promise<void> {
  const resp = await axiosInstance.put(`${DOCUMENT_PART_BASE_PATH}/align`, tgkDocumentElaborationRequest, {
    params: { generatesElaboration: true },
  });
  return resp.data;
}

export async function checkDifferences(tgkDocumentElaborationRequest: TgkDocumentElaborationRequest): Promise<void> {
  const resp = await axiosInstance.put(`${DOCUMENT_PART_BASE_PATH}/checkdifferences`, tgkDocumentElaborationRequest, {
    params: { generatesElaboration: true },
  });
  return resp.data;
}

export async function fetchParentsDocumentPart(documentPartId: string): Promise<DocumentPart[]> {
  const resp = await axiosInstance.get(`${DOCUMENT_PART_BASE_PATH}/${documentPartId}/fathers`);
  return resp.data;
}

export async function fetchAllDocumentPartStatus(documentPartId: string): Promise<DocumentPartsStatus> {
  const resp = await axiosInstance.get(`${DOCUMENT_PART_BASE_PATH}/${documentPartId}/alldocumentpartstatus`, {
    params: { notShowLoading: true },
  });
  return resp.data;
}

export async function fetchRecursiveParentsDocumentPart(documentPartId: string): Promise<DocumentPosition[]> {
  const resp = await axiosInstance.get(`${DOCUMENT_PART_BASE_PATH}/${documentPartId}/recursivefathers`, {
    params: { notShowLoading: true },
  });
  return resp.data;
}

export async function fetchRootsDocumentPart(documentPartId: string): Promise<DocumentPart[]> {
  const resp = await axiosInstance.get(`${DOCUMENT_PART_BASE_PATH}/${documentPartId}/roots`);
  return resp.data;
}

export async function fetchDocumentMetadata(
  documentPartId: string,
  throwOnMissing: boolean = true
): Promise<DocumentPartMetadata> {
  const resp = await axiosInstance.get(`${DOCUMENT_PART_METADATA_BASE_PATH}/${documentPartId}`, {
    params: { throwOnMissing },
  });
  return resp.data;
}

export async function fetchXmlMetadata(documentPartId: string, cfg?: AxiosRequestConfig): Promise<XmlDocumentMetadata> {
  const resp = await axiosInstance.get(`${DOCUMENT_PART_METADATA_BASE_PATH}/${documentPartId}/xml`, cfg);
  return resp.data;
}

export async function unlinkDocumentPart(
  docPartFatherId: string,
  unlinkedDocPartId: string,
  logicId: string
): Promise<string> {
  const resp = await axiosInstance.post(
    `${DOCUMENT_PART_BASE_PATH}/${docPartFatherId}/${unlinkedDocPartId}/${logicId}/unlink`
  );
  return resp.data;
}

export async function deleteDocumentPart(
  docPartId: string,
  docPartDriveItemId: string,
  openedFatherDocumentPartId?: string,
  deleteFromClient?: boolean
): Promise<string[]> {
  const params = {
    ...(openedFatherDocumentPartId ? { openedFatherDocumentPartId } : {}),
    ...(deleteFromClient ? { deleteContentControlFromClient: deleteFromClient } : {}),
  };

  const resp = await axiosInstance.delete(`${DOCUMENT_PART_BASE_PATH}/${docPartId}/${docPartDriveItemId}`, {
    params,
  });
  return resp.data;
}

export async function fetchDocumentsDbList(documentDbList: Object): Promise<Map<string, DocumentPartIdNamePair[]>> {
  const resp = await axiosInstance.post(`${DOCUMENT_PART_BASE_PATH}/documentsdblist`, documentDbList);
  return resp.data;
}

export async function fetchDbMapping(documentDbList: Object): Promise<CallbackMappingMessage> {
  const resp = await axiosInstance.post(`${DOCUMENT_PART_BASE_PATH}/dbmapping`, documentDbList);
  return resp.data;
}

export async function fetchNextUserRightPermission(documentId: string): Promise<UserApprover> {
  const resp = await axiosInstance.get(`${DOCUMENT_PART_BASE_PATH}/documentId/${documentId}/userRight`, {
    params: { notShowLoading: true },
  });
  return resp.data;
}

export async function updateSettings(documentId: string, settings: DocumentPartSettings): Promise<void> {
  const resp = await axiosInstance.post(`${DOCUMENT_PART_BASE_PATH}/${documentId}/settings`, settings);
  return resp.data;
}

export async function fetchDocumentPartSuperUser(documentId: string): Promise<DocumentPartWithSuperUser> {
  const resp = await axiosInstance.get(`${DOCUMENT_PART_BASE_PATH}/documentId/${documentId}/superuser`, {
    params: { notShowLoading: true },
  });
  return resp.data;
}

// TODO: checkout should be a POST (mutates server state).
// x-cache-ttl: 0 excludes this non-idempotent GET from response caching (in-flight dedup still applies, see spec §3.1).
export async function fetchCheckoutDocument(
  siteId: string,
  driveItemId: string,
  documentId: string
): Promise<UserDocumentCheckout[]> {
  const resp = await axiosInstance.get(
    `${DOCUMENT_PART_BASE_PATH}/siteId/${siteId}/driveItemId/${driveItemId}/documentId/${documentId}/checkout`,
    {
      headers: { "x-cache-ttl": "0" },
      params: { invalidateCache: ["alluserdocumentcheckout"] },
    }
  );
  return resp.data;
}

// TODO: checkin should be a POST (mutates server state).
// x-cache-ttl: 0 excludes this non-idempotent GET from response caching (in-flight dedup still applies, see spec §3.1).
export async function fetchCheckinDocument(
  siteId: string,
  driveItemId: string,
  documentId: string
): Promise<UserDocumentCheckout[]> {
  const resp = await axiosInstance.get(
    `${DOCUMENT_PART_BASE_PATH}/siteId/${siteId}/driveItemId/${driveItemId}/documentId/${documentId}/checkin`,
    {
      headers: { "x-cache-ttl": "0" },
      params: { invalidateCache: ["alluserdocumentcheckout"] },
    }
  );
  return resp.data;
}

export async function fetchCurrentUserDocumentCheckout(): Promise<UserDocumentCheckout[]> {
  const resp = await axiosInstance.get(`${DOCUMENT_PART_BASE_PATH}/userdocumentcheckout`, {
    params: { notShowLoading: true },
  });
  return resp.data;
}

export async function fetchCheckDocumentIsCheckout(
  siteId: string,
  driveItemId: string,
  documentId: string
): Promise<boolean> {
  const resp = await axiosInstance.get(
    `${DOCUMENT_PART_BASE_PATH}/siteId/${siteId}/documentId/${documentId}/driveItemId/${driveItemId}/checkdocumentischeckout`,
    {
      params: { notShowLoading: true },
    }
  );
  return resp.data;
}

export async function fetchAllUserDocumentCheckout(): Promise<UserDocumentCheckout[]> {
  const resp = await axiosInstance.get(`${DOCUMENT_PART_BASE_PATH}/alluserdocumentcheckout`, {
    params: { notShowLoading: true },
  });
  return resp.data;
}

export async function fetchRetrieveComment(documentId: string): Promise<string> {
  const resp = await axiosInstance.get(`${DOCUMENT_PART_BASE_PATH}/documentId/${documentId}/retrievecomment`, {
    params: { notShowLoading: true },
  });
  return resp.data;
}

// Post-redesign check (IDS-7053): strictly read-only, unlike fetchDocumentMetadata above. notShowLoading/notShowError
// keep the request silent because ChangeComponentSource owns its skeleton and error state. The page calls this on
// mount before loading component instances and invalidates the cached promise on unmount; a rejection is never
// cached, so Retry can re-run the check without turning a transport failure into a falsy compatibility result.
export function fetchDocumentPartVersion(documentPartId: string): Promise<DocumentPartVersion> {
  const cachedRequest = documentPartVersionCache.get(documentPartId);
  if (cachedRequest !== undefined) {
    return cachedRequest;
  }

  const request: Promise<DocumentPartVersion> = axiosInstance
    .get(`${DOCUMENT_PART_METADATA_BASE_PATH}/${documentPartId}/version`, {
      params: { notShowLoading: true, notShowError: true },
    })
    .then((resp) => resp.data);

  documentPartVersionCache.set(documentPartId, request);
  request.catch(() => {
    documentPartVersionCache.delete(documentPartId);
  });

  return request;
}

// Clears the fetchDocumentPartVersion cache for one documentPartId, or entirely when called with no argument.
// ChangeComponentSource invokes this on unmount so returning after Update, Copy, or Export/Import is not served a
// version result cached before the document migration.
export function invalidateDocumentPartVersionCache(documentPartId?: string): void {
  if (documentPartId) {
    documentPartVersionCache.delete(documentPartId);
  } else {
    documentPartVersionCache.clear();
  }
}

// Component-instance existence lookup (IDS-7053, consuming the IDS-7054 contract). Never cached - unlike the version
// check above, instance data changes as the user edits the document. Errors are left to reject the returned promise
// (never mapped to hasInstances: false) so a caller can render a distinct error state rather than a false empty state
// (docs/IDS-7053-change-component-source-button/06-error-handling.md §3).
export function fetchComponentInstances(documentId: string): Promise<ComponentInstancesResult> {
  return axiosInstance
    .get(`${COMPONENTS_BASE_PATH}/instances`, {
      params: {
        documentId,
        limit: COMPONENT_INSTANCES_EXISTENCE_LIMIT,
        notShowLoading: true,
        notShowError: true,
      },
    })
    .then((resp) => resp.data);
}

// Detailed component-instance lookup (IDS-7054), the page-owned counterpart to fetchComponentInstances above. Sends
// includeDetails=true instead of limit so the backend returns the full grouped result (containsLegacyInstances,
// groups) that Change Component Source renders - limit is never sent because it is ignored once includeDetails is
// true (docs/IDS-7054-view-component-documents/05-api-contract.md §1). Never cached - instance data changes as the
// user edits the document, and the page issues exactly one call per open (FR-001). Errors are left to reject the
// returned promise (never mapped to a resolved empty result) so the page can render its own error state with Retry
// instead of a false empty state (docs/IDS-7054-view-component-documents/06-error-handling.md §3).
export function fetchComponentInstanceDetails(documentId: string): Promise<ComponentInstancesResult> {
  return axiosInstance
    .get(`${COMPONENTS_BASE_PATH}/instances`, {
      params: {
        documentId,
        includeDetails: true,
        notShowLoading: true,
        notShowError: true,
      },
    })
    .then((resp) => resp.data);
}

export function triggerChangeTemplateElaboration(
  documentId: string,
  templateId: string,
  channelIdToNotify?: string,
  rootId?: string,
  multilinkedPartIds?: string[],
  correlationId: string = uuid.v4()
): Promise<ElaborationResponse> {
  const storeData = store.getState();
  const clientId = selectClientId(storeData);
  const userId = selectCpmUsername(storeData);
  const changeTemplateElaborationItem: ElaborationItem = {
    elaborationType: "CHANGE_TEMPLATE",
    correlationId,
    contentControlId: "",
    templateId,
    ...(rootId !== undefined ? { rootId } : {}),
    ...(multilinkedPartIds?.length ? { multilinkedPartIds } : {}),
  };

  const elaboration: ElaborationRequest = {
    correlationId,
    clientId,
    documentId,
    userId,
    channelIdToNotify: channelIdToNotify || EVENT_CHANNEL_DEFAULT_TOPIC,
    tgkElaborationItemList: [changeTemplateElaborationItem],
  };

  return createElaborationRequestAsync(elaboration);
}

// Component mapping request (IDS-7234). sourceMappings and instanceHints are built by the caller from the panel's
// own groups/pendingChanges state; this function only shapes and sends the elaboration item, exactly one per call.
export function triggerComponentMappingElaboration(
  documentId: string,
  sourceMappings: ComponentSourceMappingPair[],
  instanceHints: InstanceHint[],
  correlationId: string = uuid.v4()
): Promise<ElaborationResponse> {
  const storeData = store.getState();
  const clientId = selectClientId(storeData);
  const userId = selectCpmUsername(storeData);
  const channelIdToNotify = selectActiveEventChannelId(storeData) ?? EVENT_CHANNEL_DEFAULT_TOPIC;
  const componentMappingElaborationItem: ElaborationItem = {
    elaborationType: "COMPONENT_MAPPING",
    correlationId,
    contentControlId: "",
    documentId,
    componentMappingReference: {
      sourceMappings,
      instanceHints,
    },
  };

  const elaboration: ElaborationRequest = {
    correlationId,
    clientId,
    documentId,
    userId,
    channelIdToNotify,
    tgkElaborationItemList: [componentMappingElaborationItem],
  };

  return createElaborationRequestAsync(elaboration);
}
